import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSession } from "../../shell/session";
import { Badge, Empty, ErrorNote } from "../../ui";
import { formatTime } from "../../../shared/time";
import type { PlanSummary, ServiceType } from "../../../shared/types";
import { listPlans, listServiceTypes, type PlanListStatus } from "./client";
import { canLead, groupPlansByDay, messageOf, stageColor, statusTone, weekWindow } from "./model";
import { ScheduleBoard } from "./schedule";
import "./services.css";

type View = "plans" | "schedule" | "types";

const STATUSES: Array<{ id: PlanListStatus; label: string }> = [
  { id: "active", label: "On the board" },
  { id: "draft", label: "Draft" },
  { id: "confirmed", label: "Confirmed" },
  { id: "archived", label: "Archived" },
  { id: "all", label: "Everything" },
];

export function ServicesPage() {
  const { user } = useSession();
  const leader = canLead(user?.role);
  const [search, setSearch] = useSearchParams();
  const requested = search.get("view");
  const view: View = requested === "schedule" || requested === "types" ? requested : "plans";
  const [anchor, setAnchor] = useState(() => new Date().toISOString());
  const [types, setTypes] = useState<ServiceType[]>([]);
  const [typeError, setTypeError] = useState("");
  const range = useMemo(() => weekWindow(anchor), [anchor]);
  const tabRefs = useRef<Partial<Record<View, HTMLButtonElement | null>>>({});

  useEffect(() => {
    let cancel = false;
    void listServiceTypes()
      .then((rows) => { if (!cancel) setTypes(rows); })
      .catch((caught: unknown) => { if (!cancel) setTypeError(messageOf(caught)); });
    return () => { cancel = true; };
  }, []);

  function openView(next: View) {
    const params = new URLSearchParams(search);
    if (next === "plans") params.delete("view");
    else params.set("view", next);
    setSearch(params, { replace: true });
  }

  function onTabsKey(event: KeyboardEvent<HTMLDivElement>) {
    const order: View[] = ["plans", "schedule", "types"];
    const index = order.indexOf(view);
    const nextIndex = event.key === "ArrowRight" ? (index + 1) % order.length : event.key === "ArrowLeft" ? (index - 1 + order.length) % order.length : -1;
    const next = order[nextIndex];
    if (!next) return;
    event.preventDefault();
    openView(next);
    tabRefs.current[next]?.focus();
  }

  function shiftAnchor(days: number) {
    setAnchor((current) => new Date(new Date(current).getTime() + days * 86_400_000).toISOString());
  }

  return (
    <section className="page svc">
      <header className="page-head">
        <div>
          <svg className="svc-staff" viewBox="0 0 140 36" aria-hidden="true">
            <path d="M2 27c10-14 14-14 22 0s12 14 22 0 12-14 22 0 12 14 22 0 12-14 22 0 14-10 26-6" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
          <p className="kicker">Services</p>
          <h1 className="page-title">The order of the room</h1>
          <p className="lede">Plans, teams, and the same set the Music Stand opens. Times stay in Pacific/Auckland.</p>
        </div>
        {leader && <Link className="btn primary" to="/services/new">New plan</Link>}
      </header>
      <div className="svc-tabs" role="tablist" aria-label="Services" onKeyDown={onTabsKey}>
        {([["plans", "Plans"], ["schedule", "Schedule"], ["types", "Service types"]] as const).map(([id, label]) => (
          <button
            key={id}
            ref={(node) => { tabRefs.current[id] = node; }}
            className="svc-chip"
            type="button"
            role="tab"
            id={`services-tab-${id}`}
            aria-selected={view === id}
            aria-controls={`services-panel-${id}`}
            tabIndex={view === id ? 0 : -1}
            onClick={() => openView(id)}
          >{label}</button>
        ))}
      </div>
      {typeError && view === "types" && <ErrorNote>{typeError}</ErrorNote>}
      {view === "plans" && (
        <div role="tabpanel" id="services-panel-plans" aria-labelledby="services-tab-plans">
          <PlanBrowser types={types} leader={leader} range={range} onShift={shiftAnchor} onToday={() => setAnchor(new Date().toISOString())} />
        </div>
      )}
      {view === "schedule" && (
        <div role="tabpanel" id="services-panel-schedule" aria-labelledby="services-tab-schedule">
          <ScheduleBoard range={range} onShift={shiftAnchor} onToday={() => setAnchor(new Date().toISOString())} />
        </div>
      )}
      {view === "types" && (
        <div role="tabpanel" id="services-panel-types" aria-labelledby="services-tab-types">
          <TypeCatalog types={types} />
        </div>
      )}
    </section>
  );
}

function PlanBrowser({ types, leader, range, onShift, onToday }: {
  types: ServiceType[];
  leader: boolean;
  range: { from: string; to: string; label: string };
  onShift: (days: number) => void;
  onToday: () => void;
}) {
  const [status, setStatus] = useState<PlanListStatus>("active");
  const [typeId, setTypeId] = useState("");
  const [query, setQuery] = useState("");
  const [weekOnly, setWeekOnly] = useState(false);
  const [plans, setPlans] = useState<PlanSummary[] | null>(null);
  const [error, setError] = useState("");
  const from = weekOnly ? range.from : "";
  const to = weekOnly ? range.to : "";

  useEffect(() => {
    let cancel = false;
    setError("");
    void listPlans({ status, from: from || undefined, to: to || undefined })
      .then((rows) => { if (!cancel) setPlans(rows); })
      .catch((caught: unknown) => { if (!cancel) setError(messageOf(caught)); });
    return () => { cancel = true; };
  }, [status, from, to]);

  const visible = (plans ?? []).filter((plan) => {
    if (typeId && plan.serviceTypeId !== typeId) return false;
    const haystack = `${plan.title} ${plan.series ?? ""} ${plan.serviceTypeName}`.toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });
  const groups = groupPlansByDay(visible);

  return (
    <div className="stack">
      <div className="svc-filters">
        <label className="field svc-search">Search plans
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Title or series" />
        </label>
        <div className="cluster" role="group" aria-label="Status">
          {STATUSES.map((item) => (
            <button key={item.id} className="svc-chip" type="button" aria-pressed={status === item.id} onClick={() => setStatus(item.id)}>{item.label}</button>
          ))}
        </div>
        <div className="cluster" role="group" aria-label="Service type">
          <button className="svc-chip" type="button" aria-pressed={typeId === ""} onClick={() => setTypeId("")}>All types</button>
          {types.map((type) => (
            <button key={type.id} className="svc-chip" type="button" aria-pressed={typeId === type.id} onClick={() => setTypeId(type.id)}>{type.name}</button>
          ))}
          <button className="svc-chip" type="button" aria-pressed={weekOnly} onClick={() => setWeekOnly((value) => !value)}>This week</button>
        </div>
        {weekOnly && (
          <div className="cluster">
            <button className="btn small" type="button" onClick={() => onShift(-7)}>Previous week</button>
            <button className="btn small" type="button" onClick={onToday}>Current week</button>
            <button className="btn small" type="button" onClick={() => onShift(7)}>Next week</button>
            <span className="muted">{range.label}</span>
          </div>
        )}
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      {plans === null && <p className="muted">Loading plans…</p>}
      {plans !== null && visible.length === 0 && (
        <Empty
          title="Nothing on this board"
          body="Adjust the filters, or create a plan and the order, team, and stand will share it."
          action={leader ? <Link className="btn primary" to="/services/new">New plan</Link> : undefined}
        />
      )}
      {groups.map((group) => (
        <section className="svc-day" key={group.label}>
          <h2>{group.label}</h2>
          <div className="stack">
            {group.plans.map((plan) => (
              <Link className="svc-plan" key={plan.id} to={`/services/${plan.id}`}>
                <span className="svc-type-bar" style={{ background: stageColor(plan.serviceTypeColor) }} />
                <span className="svc-when"><strong>{formatTime(plan.startsAt)}</strong><span>until {formatTime(plan.endsAt)}</span></span>
                <span className="svc-plan-copy">
                  <strong>{plan.title}</strong>
                  <span className="muted">{plan.serviceTypeName}{plan.series ? ` · ${plan.series}` : ""} · {plan.itemCount} rows</span>
                </span>
                <span className="cluster">
                  {plan.pendingResponses > 0 && <Badge tone="amber">{plan.pendingResponses} waiting</Badge>}
                  <Badge tone={statusTone(plan.status)}>{plan.status}</Badge>
                </span>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function TypeCatalog({ types }: { types: ServiceType[] }) {
  if (types.length === 0) {
    return <Empty title="No service types" body="A plan needs a service type. This workspace does not have one yet." />;
  }
  return (
    <div className="stack">
      <p className="lede">Choose a type when you create a plan. Its teams are copied onto that plan. Creating or editing a template is not available from here.</p>
      <div className="svc-teams">
        {types.map((type) => (
          <article className="svc-type static" key={type.id}>
            <span className="svc-type-bar" style={{ background: stageColor(type.color) }} />
            <span><strong>{type.name}</strong><small className="faint">{type.description || "No description"}</small></span>
          </article>
        ))}
      </div>
    </div>
  );
}
